import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { FiStar, FiHeart, FiShoppingCart, FiArrowLeft, FiShare2, FiBookOpen } from 'react-icons/fi';

const DetailsContainer = styled.div`
  min-height: 100vh;
  background-color: #f8fafc;
  padding: 40px 0;
`;

const DetailsContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
`;

const BackButton = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  color: #6b7280;
  text-decoration: none;
  font-weight: 500;
  margin-bottom: 24px;
  transition: color 0.2s ease;
  
  &:hover {
    color: #2563eb;
  }
`;

const BookDetailsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 2fr;
  gap: 40px;
  background: white;
  border-radius: 16px;
  padding: 40px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.05);
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 24px;
    padding: 24px;
  }
`;

const BookImageSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const BookImage = styled.div`
  width: 100%;
  max-width: 400px;
  height: 500px;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15);
  margin-bottom: 24px;
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const BookActions = styled.div`
  display: flex;
  gap: 12px;
  width: 100%;
`;

const ActionButton = styled.button`
  flex: 1;
  padding: 12px 16px;
  border: 2px solid #e5e7eb;
  border-radius: 8px;
  background: white;
  color: #374151;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  
  &:hover {
    border-color: #2563eb;
    color: #2563eb;
  }
`;

const BookInfoSection = styled.div`
  display: flex;
  flex-direction: column;
`;

const BookTitle = styled.h1`
  font-size: 2.5rem;
  font-weight: 700;
  color: #1f2937;
  margin-bottom: 12px;
  line-height: 1.2;
`;

const BookAuthor = styled.p`
  font-size: 1.25rem;
  color: #6b7280;
  margin-bottom: 20px;
`;

const BookRating = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 24px;
`;

const Stars = styled.div`
  display: flex;
  gap: 4px;
  color: #fbbf24;
`;

const RatingText = styled.span`
  color: #6b7280;
  font-size: 0.875rem;
`;

const BookPrice = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 32px;
`;

const CurrentPrice = styled.span`
  font-size: 2rem;
  font-weight: 700;
  color: #1f2937;
`;

const OriginalPrice = styled.span`
  font-size: 1.25rem;
  color: #9ca3af;
  text-decoration: line-through;
`;

const Discount = styled.span`
  background: #ef4444;
  color: white;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 0.875rem;
  font-weight: 600;
`;

const BookDescription = styled.div`
  margin-bottom: 32px;
  
  h3 {
    font-size: 1.25rem;
    font-weight: 600;
    color: #1f2937;
    margin-bottom: 12px;
  }
  
  p {
    color: #6b7280;
    line-height: 1.6;
  }
`;

const BookDetailsInfo = styled.div`
  margin-bottom: 32px;
  
  h3 {
    font-size: 1.25rem;
    font-weight: 600;
    color: #1f2937;
    margin-bottom: 16px;
  }
`;

const DetailsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px;
  
  @media (max-width: 480px) {
    grid-template-columns: 1fr;
  }
`;

const DetailItem = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 12px 0;
  border-bottom: 1px solid #f3f4f6;
  
  span:first-child {
    color: #6b7280;
    font-weight: 500;
  }
  
  span:last-child {
    color: #1f2937;
    font-weight: 600;
  }
`;

const PurchaseSection = styled.div`
  display: flex;
  gap: 16px;
  flex-wrap: wrap;
`;

const AddToCartButton = styled.button`
  flex: 1;
  min-width: 200px;
  padding: 16px 24px;
  background: linear-gradient(135deg, #667eea, #764ba2);
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 1.125rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  
  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 8px 25px rgba(102, 126, 234, 0.3);
  }
`;

const BuyNowButton = styled.button`
  flex: 1;
  min-width: 200px;
  padding: 16px 24px;
  background: white;
  color: #2563eb;
  border: 2px solid #2563eb;
  border-radius: 8px;
  font-size: 1.125rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: #2563eb;
    color: white;
  }
`;

const BookDetails = ({ onAddToCart }) => {
  const { id } = useParams();
  const [book, setBook] = useState(null);
  const [loading, setLoading] = useState(true);
  const [quantity, setQuantity] = useState(1);

  // Mock data - in a real app, this would come from an API
  const mockBook = {
    id: 1,
    title: "The Great Gatsby",
    author: "F. Scott Fitzgerald",
    price: 9.99,
    originalPrice: 12.99,
    image: "https://images.unsplash.com/photo-1544947950-fa07a98d237f?w=400&h=600&fit=crop",
    category: "Fiction",
    rating: 4.5,
    reviews: 1234,
    description: "A classic American novel set in the Jazz Age, following the mysterious Jay Gatsby and his obsession with the beautiful Daisy Buchanan. This timeless tale explores themes of wealth, love, and the American Dream.",
    pages: 180,
    language: "English",
    publisher: "Scribner",
    publicationDate: "April 10, 1925",
    isbn: "978-0-7432-7356-5",
    format: "eBook",
    fileSize: "2.1 MB"
  };

  useEffect(() => {
    // Simulate API call
    setTimeout(() => {
      setBook(mockBook);
      setLoading(false);
    }, 1000);
  }, [id]);

  const handleAddToCart = () => {
    const bookToAdd = { ...book, quantity };
    onAddToCart(bookToAdd);
  };

  const handleBuyNow = () => {
    handleAddToCart();
    // Navigate to checkout
  };

  if (loading) {
    return (
      <DetailsContainer>
        <DetailsContent>
          <div style={{ textAlign: 'center', padding: '80px 0' }}>
            <h2>Loading book details...</h2>
          </div>
        </DetailsContent>
      </DetailsContainer>
    );
  }

  if (!book) {
    return (
      <DetailsContainer>
        <DetailsContent>
          <div style={{ textAlign: 'center', padding: '80px 0' }}>
            <h2>Book not found</h2>
            <p>The book you're looking for doesn't exist.</p>
          </div>
        </DetailsContent>
      </DetailsContainer>
    );
  }

  const discount = book.originalPrice 
    ? Math.round(((book.originalPrice - book.price) / book.originalPrice) * 100)
    : 0;

  return (
    <DetailsContainer>
      <DetailsContent>
        <BackButton to="/books">
          <FiArrowLeft />
          Back to Books
        </BackButton>

        <BookDetailsGrid>
          <BookImageSection>
            <BookImage>
              <img src={book.image} alt={book.title} />
            </BookImage>
            <BookActions>
              <ActionButton>
                <FiHeart />
                Add to Wishlist
              </ActionButton>
              <ActionButton>
                <FiShare2 />
                Share
              </ActionButton>
            </BookActions>
          </BookImageSection>

          <BookInfoSection>
            <BookTitle>{book.title}</BookTitle>
            <BookAuthor>by {book.author}</BookAuthor>

            <BookRating>
              <Stars>
                {[...Array(5)].map((_, i) => (
                  <FiStar
                    key={i}
                    size={20}
                    fill={i < Math.floor(book.rating) ? '#fbbf24' : 'none'}
                  />
                ))}
              </Stars>
              <RatingText>{book.rating} ({book.reviews} reviews)</RatingText>
            </BookRating>

            <BookPrice>
              <CurrentPrice>${book.price}</CurrentPrice>
              {book.originalPrice && (
                <>
                  <OriginalPrice>${book.originalPrice}</OriginalPrice>
                  <Discount>{discount}% OFF</Discount>
                </>
              )}
            </BookPrice>

            <BookDescription>
              <h3>About this book</h3>
              <p>{book.description}</p>
            </BookDescription>

            <BookDetailsInfo>
              <h3>Book Details</h3>
              <DetailsGrid>
                <DetailItem>
                  <span>Pages:</span>
                  <span>{book.pages}</span>
                </DetailItem>
                <DetailItem>
                  <span>Language:</span>
                  <span>{book.language}</span>
                </DetailItem>
                <DetailItem>
                  <span>Publisher:</span>
                  <span>{book.publisher}</span>
                </DetailItem>
                <DetailItem>
                  <span>Publication Date:</span>
                  <span>{book.publicationDate}</span>
                </DetailItem>
                <DetailItem>
                  <span>ISBN:</span>
                  <span>{book.isbn}</span>
                </DetailItem>
                <DetailItem>
                  <span>Format:</span>
                  <span>{book.format}</span>
                </DetailItem>
              </DetailsGrid>
            </BookDetailsInfo>

            <PurchaseSection>
              <AddToCartButton onClick={handleAddToCart}>
                <FiShoppingCart />
                Add to Cart
              </AddToCartButton>
              <BuyNowButton onClick={handleBuyNow}>
                <FiBookOpen />
                Buy Now
              </BuyNowButton>
            </PurchaseSection>
          </BookInfoSection>
        </BookDetailsGrid>
      </DetailsContent>
    </DetailsContainer>
  );
};

export default BookDetails;
